/**
 * Shared redaction helpers for log payloads.
 *
 * Client-side sanitizing is useful for keeping secrets out of browser-side
 * buffers, but the server must still treat `/api/logs` as untrusted input.
 * Keep the key/string rules here so both sides apply the same baseline.
 */

export const REDACTED_LOG_VALUE = '[REDACTED]';
const CIRCULAR_LOG_VALUE = '[CIRCULAR]';
const MAX_REDACTION_DEPTH = 12;

const SENSITIVE_KEY_PARTS = [
  'authentication',
  'authorization',
  'credential',
  'password',
  'passwd',
  'secret',
];

const SENSITIVE_KEYS = new Set([
  'accesstoken',
  'apikey',
  'authtoken',
  'bearer',
  'cookie',
  'idtoken',
  'jwt',
  'privatekey',
  'refreshtoken',
  'sessiontoken',
  'token',
]);

const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const BEARER_PATTERN = /\bBearer\s+[^\s,;]+/gi;
const URL_SECRET_PATTERN = /([?&](?:access_?token|api_?key|auth_?token|id_?token|jwt|password|refresh_?token|secret|token)=)[^&#\s]*/gi;
const TEXT_SECRET_PATTERN = /((?:access_?token|api_?key|auth_?token|authorization|cookie|id_?token|jwt|password|refresh_?token|secret|session_?token|token)\s*["']?\s*[:=]\s*["']?)[^\s,"'}&#?]+/gi;

export function isSensitiveLogKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (SENSITIVE_KEYS.has(normalized) || normalized.endsWith('token')) {
    return true;
  }
  return SENSITIVE_KEY_PARTS.some((part) => normalized.includes(part));
}

/** Redact common credential shapes embedded inside otherwise useful text. */
export function redactLogString(value: string): string {
  return value
    .replace(BEARER_PATTERN, `Bearer ${REDACTED_LOG_VALUE}`)
    .replace(JWT_PATTERN, REDACTED_LOG_VALUE)
    .replace(URL_SECRET_PATTERN, `$1${REDACTED_LOG_VALUE}`)
    .replace(TEXT_SECRET_PATTERN, `$1${REDACTED_LOG_VALUE}`);
}

/** Recursively sanitize structured logger metadata without mutating it. */
export function redactLogValue(
  value: unknown,
  key?: string,
  seen: WeakSet<object> = new WeakSet<object>(),
  depth = 0,
): unknown {
  if (key && isSensitiveLogKey(key)) return REDACTED_LOG_VALUE;
  if (typeof value === 'string') return redactLogString(value);
  if (typeof value === 'bigint') return value.toString();
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_REDACTION_DEPTH) return REDACTED_LOG_VALUE;
  if (seen.has(value)) return CIRCULAR_LOG_VALUE;

  seen.add(value);
  try {
    if (value instanceof Date) return value;
    if (value instanceof RegExp) return String(value);
    if (value instanceof Error) {
      const cause = (value as Error & { cause?: unknown }).cause;
      return {
        name: redactLogString(value.name),
        message: redactLogString(value.message),
        stack: value.stack ? redactLogString(value.stack) : undefined,
        cause: cause === undefined
          ? undefined
          : redactLogValue(cause, 'cause', seen, depth + 1),
      };
    }
    if (Array.isArray(value)) {
      return value.map((entry) => redactLogValue(entry, undefined, seen, depth + 1));
    }

    const output: Record<string, unknown> = {};
    for (const [entryKey, entryValue] of Object.entries(value)) {
      output[entryKey] = redactLogValue(entryValue, entryKey, seen, depth + 1);
    }
    return output;
  } catch {
    return REDACTED_LOG_VALUE;
  } finally {
    seen.delete(value);
  }
}

/**
 * Context user IDs are identifiers, never authentication material. Reject
 * credential-shaped fallbacks rather than persisting them under a misleading
 * field name.
 */
export function isSafeLogUserId(value: string): boolean {
  const candidate = value.trim();
  if (!candidate || candidate.length > 128 || /\s/.test(candidate)) {
    return false;
  }
  if (candidate.startsWith('local_') || candidate.startsWith('Bearer ')) {
    return false;
  }
  if (redactLogString(candidate) !== candidate) {
    return false;
  }
  return /^[A-Za-z0-9:._-]+$/.test(candidate);
}
